import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import {
  DEFAULT_MASS_VALIDITY_MONTHS,
  addMassValidityDaysToIsoDate,
  calculateMassBalanceResults,
  configureMassSessionStandards,
  createMassBalanceSession,
  getMassSession,
  listMassBalanceStandardOptions,
  listMassBlocks,
  listMassPoints,
  listMassResults,
  listMassSessionsByInstrument,
  massPointsToRows,
  saveMassPoints,
  updateMassSession,
} from '../../utils/api-mass-calibration';
import {
  MASS_BALANCE_TEMPLATE,
  MASS_ECCENTRICITY_KEYS,
  MASS_REPEATABILITY_KEYS,
  computeMassBalancePointMetrics,
  findBestMassWeightCombination,
  finiteOrNull,
  mergeMassFamilyData,
} from '../../utils/mass-calibration-profile';

const KEYS = [...MASS_REPEATABILITY_KEYS, ...MASS_ECCENTRICITY_KEYS];
const VALIDITY = [{ value: 6, label: '6 meses' }, { value: 12, label: '1 año' }, { value: 24, label: '2 años' }];
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmt = (value, decimals = 6) => finiteOrNull(value) === null ? '—' : Number(value).toFixed(decimals).replace('.', ',');
const emptyRows = () => Array.from({ length: MASS_BALANCE_TEMPLATE.pointCount }, (_, index) => ({
  point_no: index + 1, setpoint: '', capability_ids: [], ...Object.fromEntries(KEYS.map((key) => [key, ''])),
}));

export default function MassCalibrationPage() {
  const { instrumentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const serviceOrderCode = searchParams.get('serviceOrderCode') || '';
  const initialSessionId = searchParams.get('sessionId') || '';
  const [instrument, setInstrument] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [session, setSession] = useState(null);
  const [block, setBlock] = useState(null);
  const [rows, setRows] = useState(emptyRows());
  const [options, setOptions] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);

  const familyData = useMemo(() => mergeMassFamilyData(session?.family_data), [session?.family_data]);
  const availableNominalKg = useMemo(() => options.reduce(
    (sum, option) => sum + (finiteOrNull(option.nominal_kg) ?? 0), 0,
  ), [options]);
  const maxRequestedKg = useMemo(() => Math.max(
    0,
    ...rows.map((row) => finiteOrNull(row.setpoint) ?? 0),
  ), [rows]);
  const liveResults = useMemo(() => {
    if (!session || !snapshots.length) return [];
    return rows.map((row) => computeMassBalancePointMetrics(row, session, snapshots)).filter(Boolean);
  }, [rows, session, snapshots]);

  async function loadSessions(preferred = '') {
    const list = await listMassSessionsByInstrument(instrumentId);
    setSessions(list);
    if (preferred) setSessionId(preferred);
    else if (!sessionId && list.length) setSessionId(list[0].id);
  }

  async function loadSession(id) {
    if (!id) {
      setSession(null); setBlock(null); setRows(emptyRows()); setSnapshots([]); setResults([]);
      return;
    }
    const [loaded, blocks, points, calculated] = await Promise.all([
      getMassSession(id), listMassBlocks(id), listMassPoints(id), listMassResults(id),
    ]);
    const normalized = { ...loaded, family_data: mergeMassFamilyData(loaded.family_data) };
    const selectedBlock = blocks.find((item) => item.block_key === 'mass_balance') || blocks[0];
    setSession(normalized);
    setBlock(selectedBlock);
    setRows(selectedBlock ? massPointsToRows(selectedBlock, points, normalized) : emptyRows());
    setSnapshots(normalized.family_data.standard_configuration?.weights || []);
    setResults(calculated || []);
    setOptions(await listMassBalanceStandardOptions({ onDate: normalized.calibration_date || todayIso() }));
  }

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase.from('instruments')
          .select('*, client:client_id(id, name, client_number)').eq('id', instrumentId).single();
        if (error) throw error;
        if (!active) return;
        setInstrument(data);
        await loadSessions();
      } catch (error) {
        if (active) setNotice({ type: 'error', text: error.message });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [instrumentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sessionId) return;
    loadSession(sessionId).catch((error) => setNotice({ type: 'error', text: error.message }));
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  function patchSession(field, value) {
    setSession((current) => ({ ...current, [field]: value }));
  }

  function patchFamily(field, value) {
    setSession((current) => ({
      ...current,
      family_data: mergeMassFamilyData({ ...current.family_data, [field]: value }),
    }));
  }

  function patchRow(index, field, value) {
    setRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row));
  }

  async function handleCreate() {
    setBusy('create'); setNotice(null);
    try {
      const created = await createMassBalanceSession({ instrumentId, serviceOrderCode });
      await loadSessions(created.id);
      setNotice({ type: 'success', text: 'Sesión de Masa creada.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    } finally { setBusy(''); }
  }

  function handleAutoAssign() {
    const tolerance = Math.max(Math.abs(finiteOrNull(session?.instrument_resolution) || 0), 1e-12);
    const next = rows.map((row) => ({
      ...row,
      capability_ids: findBestMassWeightCombination(row.setpoint, options, tolerance),
    }));
    setRows(next);
    const missing = next.filter((row) => finiteOrNull(row.setpoint) !== null && !row.capability_ids.length).length;
    setNotice(missing
      ? { type: 'error', text: `${missing} punto(s) no coinciden con una combinación disponible. Ajustá el nominal o elegí los pesos manualmente.` }
      : { type: 'success', text: 'Patrones asignados según el valor nominal de cada punto.' });
  }

  async function persist(calculate) {
    if (!session || !block) return;
    setBusy(calculate ? 'calculate' : 'save'); setNotice(null);
    try {
      const pointStandards = rows.map((row) => ({ point_no: row.point_no, capability_ids: row.capability_ids || [] }));
      const capabilityIds = [...new Set(pointStandards.flatMap((row) => row.capability_ids))];
      if (calculate && !capabilityIds.length) throw new Error('Asigná al menos una masa patrón a cada punto.');
      const calibrationDate = session.calibration_date || todayIso();
      const validityMonths = Number(session.validity_months) || DEFAULT_MASS_VALIDITY_MONTHS;
      const updated = await updateMassSession(session.id, {
        instrument_range_min: finiteOrNull(session.instrument_range_min),
        instrument_range_max: finiteOrNull(session.instrument_range_max),
        instrument_resolution: finiteOrNull(session.instrument_resolution),
        instrument_class: finiteOrNull(session.instrument_class),
        calibration_date: calibrationDate,
        validity_months: validityMonths,
        valid_until: addMassValidityDaysToIsoDate(calibrationDate, validityMonths),
        calibration_location: session.calibration_location || null,
        notes: session.notes || null,
        family_data: mergeMassFamilyData({ ...familyData, point_standards }),
      });
      let effectiveSnapshots = snapshots;
      if (capabilityIds.length) {
        const configured = await configureMassSessionStandards(session.id, capabilityIds);
        effectiveSnapshots = configured?.configuration?.weights || configured?.weights || [];
        setSnapshots(effectiveSnapshots);
      }
      await saveMassPoints(session.id, block, rows, effectiveSnapshots);
      if (calculate) {
        await calculateMassBalanceResults(session.id);
      }
      await loadSession(session.id);
      await loadSessions(session.id);
      setNotice({ type: 'success', text: calculate ? 'Cálculo finalizado y enviado a Certificados a aprobar.' : 'Borrador guardado.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message });
    } finally { setBusy(''); }
  }

  if (loading) return <div className="p-8 text-slate-200">Cargando plantilla de Masa…</div>;
  const calibrationDate = session?.calibration_date || todayIso();
  const validUntil = addMassValidityDaysToIsoDate(calibrationDate, session?.validity_months || DEFAULT_MASS_VALIDITY_MONTHS);

  return (
    <div className="p-6 space-y-6 max-w-[1700px] mx-auto">
      <style>{`.input-mass{display:block;width:100%;margin-top:.25rem;border:1px solid rgb(203 213 225);border-radius:.25rem;background:#fff;padding:.375rem .5rem;color:#0f172a}.mass-cell{border:1px solid rgba(255,255,255,.12);padding:.5rem}`}</style>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-fuchsia-300">Masa · Balanza monoplato</div>
          <h1 className="text-2xl font-semibold text-white">Calibración de balanzas</h1>
          <p className="text-sm text-slate-300 mt-1">Instrumento <strong>{instrument?.tag || instrument?.lab_id || instrumentId}</strong> · {instrument?.brand || '—'} {instrument?.model || ''} · Cliente: {instrument?.client?.name || '—'}</p>
        </div>
        <button type="button" onClick={() => navigate(`/ops/instrument-details/${instrumentId}`)} className="px-3 py-2 rounded-lg border border-white/15 text-slate-200">Volver al instrumento</button>
      </div>

      {notice && <div className={`rounded-lg border px-4 py-3 text-sm ${notice.type === 'success' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-rose-500/30 bg-rose-500/10 text-rose-200'}`}>{notice.text}</div>}

      <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <label className="text-xs text-slate-300">Sesión
            <select value={sessionId} onChange={(event) => setSessionId(event.target.value)} className="block mt-1 min-w-[360px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900">
              <option value="">Seleccionar…</option>
              {sessions.map((item) => <option key={item.id} value={item.id}>{new Date(item.created_at).toLocaleString('es-AR')} · {item.status}</option>)}
            </select>
          </label>
          <button type="button" disabled={!!busy} onClick={handleCreate} className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium disabled:opacity-60">{busy === 'create' ? 'Creando…' : 'Nueva sesión de Masa'}</button>
        </div>
        {session && <>
          {session.review_notes && <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200"><strong>Observación del jefe:</strong> {session.review_notes}</div>}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            <ReadField label="Estado" value={session.status} />
            <NumberField label="Mínimo (kg)" value={session.instrument_range_min} onChange={(value) => patchSession('instrument_range_min', value)} />
            <NumberField label="Máximo (kg)" value={session.instrument_range_max} onChange={(value) => patchSession('instrument_range_max', value)} />
            <NumberField label="Resolución (kg)" value={session.instrument_resolution} onChange={(value) => patchSession('instrument_resolution', value)} />
            <NumberField label="Tolerancia abs. (kg)" value={session.instrument_class} onChange={(value) => patchSession('instrument_class', value)} />
            <label className="text-xs text-slate-300">Clase<select value={familyData.balance_class || ''} onChange={(event) => patchFamily('balance_class', event.target.value)} className="input-mass"><option value="">Sin declarar</option>{['I', 'II', 'III', 'IV'].map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
            <label className="text-xs text-slate-300">Fecha<input type="date" value={calibrationDate} onChange={(event) => patchSession('calibration_date', event.target.value)} className="input-mass" /></label>
            <label className="text-xs text-slate-300">Validez<select value={session.validity_months || 12} onChange={(event) => patchSession('validity_months', Number(event.target.value))} className="input-mass">{VALIDITY.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select><span className="block mt-1 text-[11px] text-slate-400">Hasta {validUntil}</span></label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextField label="Lugar de calibración" value={session.calibration_location || ''} onChange={(value) => patchSession('calibration_location', value)} />
            <TextField label="Observaciones" value={session.notes || ''} onChange={(value) => patchSession('notes', value)} />
          </div>
        </>}
      </section>

      {session && <>
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <div className="flex justify-between gap-3 flex-wrap"><div><h2 className="text-lg font-semibold text-white">Instrumentos patrones</h2><p className="text-xs text-slate-400">{options.length} masa(s) patrón vigente(s) para la fecha · suma nominal disponible: {fmt(availableNominalKg)} kg.</p></div><div className="flex gap-2"><button type="button" onClick={handleAutoAssign} className="px-3 py-2 rounded-lg bg-fuchsia-700 text-white text-sm">Autoasignar patrones</button><button type="button" onClick={() => navigate('/quality/reference-standards')} className="px-3 py-2 rounded-lg border border-white/15 text-slate-200 text-sm">Abrir Instrumentos Patrones</button></div></div>
          {!options.length && <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">No hay masas patrón vigentes para esta fecha.</div>}
          {maxRequestedKg > availableNominalKg && options.length > 0 && <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">La carga máxima solicitada ({fmt(maxRequestedKg)} kg) supera la suma de patrones verificados disponible ({fmt(availableNominalKg)} kg). Para ese punto deben registrarse y verificarse las cargas auxiliares utilizadas por el laboratorio antes de calcular.</div>}
        </section>

        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <div><h2 className="text-lg font-semibold text-white">Toma de datos</h2><p className="text-xs text-slate-400">A1–A5: repetibilidad · B–E: cuatro posiciones de excentricidad · las nueve lecturas son obligatorias y todos los valores se expresan en kg.</p></div>
          <div className="overflow-auto"><table className="min-w-[1500px] w-full border-collapse text-xs">
            <thead><tr className="bg-slate-800 text-slate-100">{['Pto.', 'Nominal kg', 'Pesas patrón', 'A1', 'A2', 'A3', 'A4', 'A5', 'B', 'C', 'D', 'E'].map((label) => <th key={label} className="border border-white/15 px-2 py-2">{label}</th>)}</tr></thead>
            <tbody>{rows.map((row, rowIndex) => <tr key={row.point_no}>
              <td className="mass-cell text-center text-slate-300">{row.point_no}</td>
              <Measurement value={row.setpoint} onChange={(value) => patchRow(rowIndex, 'setpoint', value)} />
              <td className="mass-cell min-w-[270px]"><select multiple size={3} value={row.capability_ids || []} onChange={(event) => patchRow(rowIndex, 'capability_ids', [...event.target.selectedOptions].map((option) => option.value))} className="w-full rounded border border-slate-300 bg-white px-1 py-1 text-slate-900">{options.map((option) => <option key={option.capability_id} value={option.capability_id}>{option.code} · {fmt(option.nominal_kg)} kg · Cert. {option.certificate_no}</option>)}</select></td>
              {KEYS.map((key) => <Measurement key={key} value={row[key]} onChange={(value) => patchRow(rowIndex, key, value)} />)}
            </tr>)}</tbody>
          </table></div>
          <p className="text-[11px] text-slate-400">Para seleccionar varias pesas, usá Ctrl + clic. El valor certificado corregido se toma directamente de cada certificado.</p>
        </section>

        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <h2 className="text-lg font-semibold text-white">Condiciones ambientales</h2>
          <div className="overflow-auto"><table className="min-w-[700px] w-full text-xs border-collapse"><thead><tr className="bg-slate-800">{['Momento', 'Temperatura °C', 'Presión hPa', 'Humedad %HR'].map((label) => <th key={label} className="mass-cell">{label}</th>)}</tr></thead><tbody>{familyData.environment_readings.map((reading, index) => <tr key={reading.key}><td className="mass-cell text-slate-300">{reading.label}</td>{['temp_c', 'pressure_hpa', 'humidity_rh'].map((field) => <Measurement key={field} value={reading[field]} onChange={(value) => { const next = familyData.environment_readings.map((item, rowIndex) => rowIndex === index ? { ...item, [field]: value } : item); patchFamily('environment_readings', next); }} />)}</tr>)}</tbody></table></div>
        </section>

        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <h2 className="text-lg font-semibold text-white">Resultados en vivo</h2>
          {!liveResults.length ? <p className="text-sm text-slate-400">Asigná patrones y cargá las cinco lecturas para calcular.</p> : <div className="overflow-auto"><table className="min-w-[1100px] w-full text-xs border-collapse"><thead><tr className="bg-slate-800">{['Pto.', 'Patrón', 'Instrumento', 'Error', 'Corrección', 'Repetibilidad', 'Excentricidad', 'U (k=2)', 'Conforme'].map((label) => <th key={label} className="mass-cell">{label}</th>)}</tr></thead><tbody>{liveResults.map((result) => <tr key={result.point_no} className={result.in_tolerance === false ? 'bg-rose-500/10' : ''}><td className="mass-cell">{result.point_no}</td><td className="mass-cell">{fmt(result.pattern_mean)}</td><td className="mass-cell">{fmt(result.instrument_mean)}</td><td className="mass-cell">{fmt(result.error_abs)}</td><td className="mass-cell">{fmt(result.correction)}</td><td className="mass-cell">{fmt(result.repeatability)}</td><td className="mass-cell">{fmt(result.eccentricity_error)}</td><td className="mass-cell font-semibold">{fmt(result.u_reported)}</td><td className="mass-cell text-center">{result.in_tolerance == null ? '—' : result.in_tolerance ? '✓' : '✕'}</td></tr>)}</tbody></table></div>}
          <div className="flex gap-3 flex-wrap"><button type="button" disabled={!!busy} onClick={() => persist(false)} className="px-4 py-2 rounded-lg border border-slate-400/50 bg-white/5 text-white font-semibold disabled:opacity-60">{busy === 'save' ? 'Guardando…' : 'Guardar borrador'}</button><button type="button" disabled={!!busy} onClick={() => persist(true)} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-60">{busy === 'calculate' ? 'Calculando…' : 'Calcular y enviar a aprobación'}</button>{results.length > 0 && <span className="self-center text-sm text-emerald-300">✓ Resultados calculados</span>}</div>
        </section>
      </>}
    </div>
  );
}

function Measurement({ value, onChange }) {
  return <td className="mass-cell p-1"><input type="text" inputMode="decimal" value={value ?? ''} onChange={(event) => onChange(event.target.value)} className="w-full min-w-[88px] rounded border border-slate-300 bg-white px-2 py-1.5 text-right text-slate-900" /></td>;
}

function NumberField({ label, value, onChange }) {
  return <label className="text-xs text-slate-300">{label}<input type="number" step="any" value={value ?? ''} onChange={(event) => onChange(event.target.value)} className="input-mass" /></label>;
}

function TextField({ label, value, onChange }) {
  return <label className="text-xs text-slate-300">{label}<input type="text" value={value ?? ''} onChange={(event) => onChange(event.target.value)} className="input-mass" /></label>;
}

function ReadField({ label, value }) {
  return <div><div className="text-xs text-slate-300">{label}</div><div className="mt-1 min-h-[34px] rounded border border-white/15 bg-black/10 px-2 py-1.5 text-sm text-slate-100">{value ?? '—'}</div></div>;
}
